import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth, authFetch } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { User, Package, FileText, LogOut, Loader2, Clock, CheckCircle, Truck, XCircle, MessageCircle } from 'lucide-react';
import { toast } from 'sonner';
import { companyInfo } from '../data/mock';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const MeinKonto = () => {
  const navigate = useNavigate();
  const { user, loading, logout, isAuthenticated } = useAuth();
  const [orders, setOrders] = useState([]);
  const [loadingOrders, setLoadingOrders] = useState(true);

  // Redirect if not logged in
  useEffect(() => {
    if (!loading && !isAuthenticated) {
      navigate('/anmelden');
    }
  }, [loading, isAuthenticated, navigate]);

  // Fetch orders
  useEffect(() => {
    const fetchOrders = async () => {
      if (!user) return;
      
      try {
        const response = await authFetch(`${API_URL}/api/orders/my-orders`);
        
        if (response.ok) {
          const data = await response.json();
          setOrders(data);
        }
      } catch (error) {
        console.error('Error fetching orders:', error);
      } finally {
        setLoadingOrders(false);
      }
    };

    if (isAuthenticated) {
      fetchOrders();
    }
  }, [user, isAuthenticated]);

  const handleLogout = async () => {
    await logout();
    toast.success('Erfolgreich abgemeldet');
    navigate('/');
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'pending':
        return <Clock className="text-yellow-500" size={20} />;
      case 'confirmed':
        return <CheckCircle className="text-blue-500" size={20} />;
      case 'shipped':
        return <Truck className="text-green-500" size={20} />;
      case 'delivered':
        return <CheckCircle className="text-green-600" size={20} />;
      case 'cancelled':
        return <XCircle className="text-red-500" size={20} />;
      default:
        return <Clock className="text-gray-500" size={20} />;
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'pending':
        return 'Ausstehend';
      case 'confirmed':
        return 'Bestätigt';
      case 'shipped':
        return 'Versendet';
      case 'delivered':
        return 'Geliefert';
      case 'cancelled':
        return 'Storniert';
      default:
        return status;
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('de-DE', {
      style: 'currency',
      currency: 'EUR'
    }).format(price);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Loader2 className="w-12 h-12 animate-spin text-red-600" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return null;
  }

  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-6xl mx-auto">
          {/* Header */}
          <div className="mb-8">
            <h1 className="text-3xl font-bold text-gray-900 mb-2">Mein Konto</h1>
            <p className="text-gray-600">Willkommen zurück, {user?.name || user?.email}</p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Sidebar */}
            <div className="lg:col-span-1 space-y-4">
              {/* Profile Card */}
              <Card className="border-0 shadow-lg">
                <CardContent className="pt-6">
                  <div className="flex items-center space-x-4 mb-6">
                    {user?.picture ? (
                      <img 
                        src={user.picture} 
                        alt={user.name} 
                        className="w-16 h-16 rounded-full"
                      />
                    ) : (
                      <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center">
                        <User className="w-8 h-8 text-red-600" />
                      </div>
                    )}
                    <div>
                      <h3 className="font-semibold text-gray-900">{user?.name || 'Benutzer'}</h3>
                      <p className="text-sm text-gray-500">{user?.email}</p>
                    </div>
                  </div>
                  
                  <div className="space-y-2">
                    <Button
                      variant="outline"
                      className="w-full justify-start"
                      onClick={() => document.getElementById('orders-section').scrollIntoView({ behavior: 'smooth' })}
                    >
                      <Package className="mr-2" size={18} />
                      Meine Bestellungen
                    </Button>
                    
                    <a 
                      href={`https://wa.me/49${companyInfo.mobile.replace(/\s/g, '').replace(/^0/, '')}?text=Hallo%2C%20ich%20habe%20eine%20Frage%20zu%20meiner%20Bestellung.`}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <Button
                        variant="outline"
                        className="w-full justify-start text-green-600 border-green-600 hover:bg-green-50"
                      >
                        <MessageCircle className="mr-2" size={18} />
                        WhatsApp Support
                      </Button>
                    </a>
                    
                    <Button
                      variant="outline"
                      className="w-full justify-start text-red-600 hover:bg-red-50"
                      onClick={handleLogout}
                    >
                      <LogOut className="mr-2" size={18} />
                      Abmelden
                    </Button>
                  </div>
                </CardContent>
              </Card>

              {/* Quick Contact */}
              <Card className="border-0 shadow-lg bg-gray-900 text-white">
                <CardContent className="pt-6">
                  <h3 className="font-semibold mb-4">Fragen zu Ihrer Bestellung?</h3>
                  <p className="text-sm text-gray-300 mb-4">Wir sind für Sie da!</p>
                  <div className="space-y-2 text-sm">
                    <a href={`tel:${companyInfo.phone.replace(/\s/g, '')}`} className="flex items-center hover:text-red-400">
                      ☎️ {companyInfo.phone}
                    </a>
                    <a href={`tel:${companyInfo.mobile.replace(/\s/g, '')}`} className="flex items-center hover:text-red-400">
                      📱 {companyInfo.mobile}
                    </a>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Main Content */}
            <div className="lg:col-span-2">
              <Card className="border-0 shadow-lg" id="orders-section">
                <CardHeader>
                  <CardTitle className="flex items-center">
                    <Package className="mr-2" size={24} />
                    Meine Bestellungen
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {loadingOrders ? (
                    <div className="text-center py-8">
                      <Loader2 className="w-8 h-8 animate-spin text-red-600 mx-auto" />
                    </div>
                  ) : orders.length === 0 ? (
                    <div className="text-center py-12">
                      <Package className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                      <h3 className="text-lg font-medium text-gray-900 mb-2">Noch keine Bestellungen</h3>
                      <p className="text-gray-500 mb-6">Stöbern Sie in unserem Sortiment und finden Sie die passende Maschine.</p>
                      <Link to="/maschinen">
                        <Button className="bg-red-600 hover:bg-red-700">
                          Maschinen ansehen
                        </Button>
                      </Link>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {orders.map((order) => (
                        <div key={order.order_id} className="border rounded-lg p-4 hover:border-red-200 transition-colors">
                          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-3">
                            <div>
                              <p className="font-medium text-gray-900">
                                {order.order_number || `#${order.order_id?.slice(-8).toUpperCase()}`}
                              </p>
                              <p className="text-sm text-gray-500">
                                {formatDate(order.created_at)}
                              </p>
                            </div>
                            <div className="flex items-center gap-4 mt-2 sm:mt-0">
                              {/* Payment Status */}
                              <div className={`text-xs px-2 py-1 rounded-full ${
                                order.payment_status === 'paid' 
                                  ? 'bg-green-100 text-green-700' 
                                  : order.payment_status === 'awaiting_payment'
                                  ? 'bg-yellow-100 text-yellow-700'
                                  : 'bg-gray-100 text-gray-600'
                              }`}>
                                {order.payment_status === 'paid' ? 'Bezahlt' : 
                                 order.payment_status === 'awaiting_payment' ? 'Zahlung offen' : 
                                 'Ausstehend'}
                              </div>
                              {/* Order Status */}
                              <div className="flex items-center space-x-2">
                                {getStatusIcon(order.status)}
                                <span className="text-sm font-medium">
                                  {getStatusText(order.status)}
                                </span>
                              </div>
                            </div>
                          </div>
                          
                          {/* Payment Method Badge */}
                          <div className="mb-3">
                            <span className={`text-xs px-2 py-1 rounded ${
                              order.payment_method === 'paypal' 
                                ? 'bg-blue-100 text-blue-700' 
                                : 'bg-gray-100 text-gray-600'
                            }`}>
                              {order.payment_method === 'paypal' ? 'PayPal' : 'SEPA-Überweisung'}
                            </span>
                          </div>
                          
                          {/* Order Items */}
                          <div className="bg-gray-50 rounded-lg p-3 mb-3">
                            {order.items?.map((item, idx) => (
                              <div key={idx} className="flex justify-between text-sm py-1">
                                <span>{item.quantity}x {item.product_name || item.name}</span>
                                <span className="font-medium">{formatPrice((item.unit_price || item.price || 0) * (item.quantity || 1))}</span>
                              </div>
                            ))}
                            <div className="border-t pt-2 mt-2 flex justify-between font-semibold">
                              <span>Gesamt</span>
                              <span className="text-red-600">{formatPrice(order.total_amount)}</span>
                            </div>
                          </div>

                          {/* Tracking Info */}
                          {order.tracking_number && (
                            <div className="bg-green-50 border border-green-200 rounded-lg p-3 mb-3">
                              <p className="text-sm font-medium text-green-800">
                                Tracking-Nummer: {order.tracking_number}
                              </p>
                              {order.tracking_carrier && (
                                <p className="text-sm text-green-600">
                                  Versand via: {order.tracking_carrier}
                                </p>
                              )}
                            </div>
                          )}

                          {/* Actions */}
                          <div className="flex flex-wrap gap-2">
                            {order.invoice_url && (
                              <a href={`${API_URL}${order.invoice_url}`} target="_blank" rel="noopener noreferrer">
                                <Button variant="outline" size="sm" data-testid={`download-invoice-${order.order_id}`}>
                                  <FileText className="mr-1" size={16} />
                                  Rechnung
                                </Button>
                              </a>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MeinKonto;
